import { Location } from "@angular/common";
import { Component, computed, OnInit, signal } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatTableModule } from "@angular/material/table";
import { MatTabsModule } from "@angular/material/tabs";
import { ActivatedRoute, Router } from "@angular/router";
import { ImageUrlDirective } from "@app/directives";
import { decimalNumber } from "@app/regex";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "@components/filters/form-autocomplete/form-autocomplete.component";
import { IApiResponse } from "@models/api-response.model";
import { AppStateService, RequestService } from "@services/index";
import { passwordValidator } from "./password.validator";
import { VendorDeliveryInfoComponent } from "./vendor-delivery-info.component";
import { VendorPerformanceComponent } from "./vendor-performance.component";
import { VendorProductSyncComponent } from "./vendor-product-sync.component";
import { VendorShopGuidelineComponent } from "./vendor-shop-guideline.component";

interface IVendorDeliveryInfo {
  iso2: string;
  deliveryText: string;
  standardDeliveryFee: number;
  destinationTranshipperMarkup: number;
}

interface DeliveryTableRow {
  iso2: string;
  formGroup: FormGroup;
}

@Component({
  selector: "fh-vendor-detail",
  templateUrl: "./vendor-detail.component.html",
  styleUrls: ["./vendor-detail.component.scss"],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    FormAutocompleteComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatTabsModule,
    MatTableModule,
    MatDialogModule,
    VendorDeliveryInfoComponent,
    VendorPerformanceComponent,
    VendorShopGuidelineComponent,
    VendorProductSyncComponent,
    MatIconModule,
    ImageUrlDirective,
  ],
})
export class VendorDetailComponent implements OnInit {
  vendorForm!: FormGroup;
  deliveryRows = signal<DeliveryTableRow[]>([]);
  countries = computed(() => this.appStateService.allCountries());
  destinationCountries = computed(() => this.appStateService.destinationCountries());

  iso2Options = computed<FormAutocompleteOption<string>[]>(() =>
    this.countries().map((c) => ({ value: c.iso2, label: `${c.name} (${c.iso2})` }))
  );
  phoneCountryCodeOptions = computed<FormAutocompleteOption<string>[]>(() =>
    this.countries().map((c) => ({
      value: `+${c.phonecode}`,
      label: `${c.name} (+${c.phonecode})`,
      displayLabel: `+${c.phonecode}`, // Same as value - just show the code
    }))
  );

  statusOptions: FormAutocompleteOption<string>[] = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  displayedColumns: string[] = [
    "iso2",
    "deliveryText",
    "standardDeliveryFee",
    "destinationTranshipperMarkup",
    "actions",
  ];

  vendorId = signal<string>("");
  /** The merchant sees customers' phone numbers (shipping labels, order emails). */
  showCustomerContact = signal(false);
  contactStatus = signal("");
  /** The checkbox is held while its save is in flight, so clicks cannot race. */
  savingContact = signal(false);
  /** Shoppers can message this store (Message button on its products and store page). */
  chatEnabled = signal(true);
  chatStatus = signal("");
  savingChat = signal(false);
  vendorEmail = signal<string>("");

  /**
   * Vendors with a Product Sync, from the backend's provider registry (the same list the
   * Product Sync page shows), so a newly added sync gets the tab without touching this file.
   * The API refuses other vendors too; hiding the tab is convenience, not the control.
   */
  private readonly productSyncVendorIds = signal<number[]>([]);

  showProductSync = computed(() => this.productSyncVendorIds().includes(Number(this.vendorId())));
  error = signal<string>("");
  success = signal<string>("");
  isLoading = signal<boolean>(false);
  profilePicUrl = signal<string>("");
  profilePicBase64 = signal<string | ArrayBuffer | null>(null);

  constructor(
    private formBuilder: FormBuilder,
    private requestService: RequestService,
    private router: Router,
    private route: ActivatedRoute,
    private appStateService: AppStateService,
    private location: Location
  ) {}

  private loadProductSyncVendors() {
    this.requestService.get<IApiResponse>("/api/v2/product-sync/providers").subscribe({
      next: (res) => {
        if (!res.status || !res.data) return;
        const list = (res.data as { providers: { vendorId: number }[] }).providers;
        this.productSyncVendorIds.set(list.map((p) => Number(p.vendorId)));
      },
    });
  }

  ngOnInit() {
    this.loadProductSyncVendors();
    this.vendorId.set(this.route.snapshot.params.id);
    this.initializeVendorForm();

    if (this.vendorId()) {
      this.getVendorDetail();
      this.initializeDeliveryRows();
      this.getVendorDeliveryInfo();
    }
  }

  private initializeVendorForm() {
    this.vendorForm = this.formBuilder.group({
      first_name: ["", Validators.required],
      last_name: [""],
      email: ["", [Validators.required, Validators.email]],
      street: [""],
      city: [""],
      state: [""],
      iso2: ["", Validators.required],
      phone_country_code: ["", Validators.required],
      phone_number: ["", Validators.required],
      status: ["active"],
      isStarSeller: [false],
      postal_code: [""],
      password: ["", [passwordValidator]],
      // The store description under the logo on the store page; the merchant
      // can also edit it in their portal's Profile.
      description: ["", [Validators.maxLength(150)]],
    });
  }
  goBack(): void {
    this.location.back();
  }
  private initializeDeliveryRows() {
    this.deliveryRows.set(
      this.destinationCountries().map((country) => ({
        iso2: country.iso2,
        formGroup: this.createDeliveryFormGroup(),
      }))
    );
  }

  private createDeliveryFormGroup(data?: Partial<IVendorDeliveryInfo>): FormGroup {
    return this.formBuilder.group({
      deliveryText: [
        data?.deliveryText ||
          "Delivery takes 3 to 7 working days. Delivery fees will be shown upon checkout.",
        Validators.required,
      ],
      standardDeliveryFee: [
        data?.standardDeliveryFee || 0,
        [Validators.required, Validators.min(0), Validators.pattern(decimalNumber)],
      ],
      destinationTranshipperMarkup: [
        data?.destinationTranshipperMarkup || 0,
        [Validators.required, Validators.min(0), Validators.pattern(decimalNumber)],
      ],
    });
  }

  /** Saved at once, apart from the form: whether this merchant sees customers' phones. */
  setShowCustomerContact(show: boolean): void {
    if (this.savingContact()) return;
    const before = this.showCustomerContact();
    this.showCustomerContact.set(show);
    this.savingContact.set(true);
    this.contactStatus.set("Saving...");
    this.requestService
      .put<IApiResponse>(`/api/v2/vendor/${this.vendorId()}/customer-contact`, { show })
      .subscribe({
        next: (res) => {
          this.savingContact.set(false);
          if (!res.status) this.showCustomerContact.set(before);
          this.contactStatus.set(res.message || (res.status ? "Saved" : "Not saved"));
        },
        error: () => {
          this.savingContact.set(false);
          this.showCustomerContact.set(before);
          this.contactStatus.set("Not saved - try again");
        },
      });
  }

  /** Saved at once, apart from the form: whether shoppers can message this store. */
  setChatEnabled(enabled: boolean): void {
    if (this.savingChat()) return;
    const before = this.chatEnabled();
    this.chatEnabled.set(enabled);
    this.savingChat.set(true);
    this.chatStatus.set("Saving...");
    this.requestService
      .put<IApiResponse>(`/api/v2/vendor/${this.vendorId()}/chat`, { enabled })
      .subscribe({
        next: (res) => {
          this.savingChat.set(false);
          if (!res.status) this.chatEnabled.set(before);
          this.chatStatus.set(
            res.status
              ? enabled
                ? "Messaging enabled"
                : "Messaging disabled"
              : res.message || "Not saved"
          );
        },
        error: () => {
          this.savingChat.set(false);
          this.chatEnabled.set(before);
          this.chatStatus.set("Not saved - try again");
        },
      });
  }

  private getVendorDetail() {
    this.requestService.get<IApiResponse>(`/api/v2/vendor/${this.vendorId()}`).subscribe((res) => {
      if (res.status) {
        const vendorData = res.data;
        delete vendorData.password;
        // Map API field names to form control names
        this.vendorForm.patchValue({
          ...vendorData,
          street: vendorData.address_line1 || "",
          isStarSeller: vendorData.is_star_seller === "Yes",
        });
        this.vendorEmail.set(String(vendorData.email ?? ""));
        this.showCustomerContact.set(Boolean(vendorData.show_customer_contact));
        this.chatEnabled.set(vendorData.chat_enabled !== false && vendorData.chat_enabled !== 0);
        if (vendorData.profile_pic) {
          this.profilePicUrl.set(vendorData.profile_pic);
        }
      }
    });
  }

  private getVendorDeliveryInfo() {
    this.requestService
      .get<IApiResponse>(`/api/v2/vendor-delivery/${this.vendorId()}`)
      .subscribe((res) => {
        if (res.status && res.data) {
          const deliveryInfo = res.data as IVendorDeliveryInfo[];

          // Update form groups with existing data
          deliveryInfo.forEach((info) => {
            const row = this.deliveryRows().find((r) => r.iso2 === info.iso2);
            if (row) {
              row.formGroup.patchValue({
                deliveryText: info.deliveryText,
                standardDeliveryFee: info.standardDeliveryFee,
                destinationTranshipperMarkup: info.destinationTranshipperMarkup,
              });
            }
          });
        }
      });
  }

  saveVendorDeliveryInfo(iso2: string) {
    const row = this.deliveryRows().find((r) => r.iso2 === iso2);
    if (!row || row.formGroup.invalid) return;

    const confirmSave = window.confirm("Are you sure you want to save this delivery information?");
    if (!confirmSave) return;

    const deliveryInfo: IVendorDeliveryInfo = {
      iso2,
      ...row.formGroup.value,
    };

    this.requestService
      .put<IApiResponse>(`/api/v2/vendor-delivery/${this.vendorId()}`, deliveryInfo)
      .subscribe((res) => {
        if (res.status) {
          this.success.set("Delivery information saved successfully");
          setTimeout(() => this.success.set(""), 3000);
        } else {
          this.error.set(res.message || "Failed to save delivery information");
          setTimeout(() => this.error.set(""), 3000);
        }
      });
  }

  saveVendor() {
    if (this.vendorForm.invalid) {
      this.vendorForm.markAllAsTouched();
      this.error.set("Please fix the errors highlighted below before saving.");
      return;
    }

    this.error.set("");
    const vendorData = this.vendorForm.value;
    if (!vendorData.password) {
      delete vendorData.password;
    }

    const api = this.vendorId() ? `/api/v2/vendor/${this.vendorId()}` : "/api/v2/vendor";

    this.requestService.post<IApiResponse>(api, vendorData).subscribe((res) => {
      if (res.status) {
        this.router.navigate(["/vendors"]);
      } else {
        this.error.set(res.message || "Failed to save vendor information");
      }
    });
  }

  onProfilePicFileInput(event: any) {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    const filename = file.name;
    const ext = filename.split(".").pop()?.toLowerCase();

    if (!["png", "jpg", "jpeg"].includes(ext || "")) {
      this.error.set("File format is not valid. Please use PNG, JPG, or JPEG.");
      setTimeout(() => this.error.set(""), 3000);
      return;
    }

    reader.readAsDataURL(file);
    reader.onload = () => {
      this.profilePicBase64.set(reader.result);
    };
  }

  uploadProfilePicture(fileInput: HTMLInputElement) {
    if (!fileInput.files || fileInput.files.length === 0) {
      return;
    }
    const event = { target: fileInput };
    this.onProfilePicFileInput(event);
  }

  saveProfilePicture() {
    if (!this.profilePicBase64()) {
      this.error.set("Please select an image first");
      setTimeout(() => this.error.set(""), 3000);
      return;
    }

    const formData = new FormData();
    const fileInput = document.querySelector(
      'input[type="file"][name="profile_pic"]'
    ) as HTMLInputElement;
    if (fileInput?.files?.[0]) {
      formData.append("profile_pic", fileInput.files[0]);
    }

    this.requestService
      .put<IApiResponse>(
        `/api/v2/vendor/upload_profile_image?vendorId=${this.vendorId()}`,
        formData
      )
      .subscribe({
        next: (res) => {
          if (res.status) {
            this.success.set("Profile picture updated successfully");
            this.profilePicUrl.set(res.data.profile_pic);
            this.profilePicBase64.set(null);
            setTimeout(() => this.success.set(""), 3000);
          } else {
            this.error.set(res.message || "Failed to update profile picture");
            setTimeout(() => this.error.set(""), 3000);
          }
        },
        error: (error) => {
          console.error("Error uploading profile picture:", error);
          this.error.set("Failed to upload profile picture");
          setTimeout(() => this.error.set(""), 3000);
        },
      });
  }

  deleteProfilePicture() {
    if (!confirm("Are you sure you want to delete the profile picture?")) {
      return;
    }

    this.requestService
      .put<IApiResponse>(`/api/v2/vendor/delete_profile_image?vendorId=${this.vendorId()}`, {})
      .subscribe({
        next: (res) => {
          if (res.status) {
            this.success.set("Profile picture deleted successfully");
            this.profilePicUrl.set("");
            this.profilePicBase64.set(null);
            setTimeout(() => this.success.set(""), 3000);
          } else {
            this.error.set(res.message || "Failed to delete profile picture");
            setTimeout(() => this.error.set(""), 3000);
          }
        },
        error: (error) => {
          console.error("Error deleting profile picture:", error);
          this.error.set("Failed to delete profile picture");
          setTimeout(() => this.error.set(""), 3000);
        },
      });
  }
}
